import { memo, useCallback, useMemo } from 'react';
import { useResponsive } from 'app/hooks';
import { integerDeclination } from 'app/pages/request-single-page/utils';
import {
  HStack,
  Media,
  MediaItem,
  message,
  Text,
  VStack,
} from 'app/pages/request-single-page/components-v4';
import { Empty } from '../../empty';
import { UploadButton } from '../../upload-button';
import { ThumbnailsControls } from '../../thumbnails-controls';

type BodyProps = {
  committedFilesLength: number;
  draftFiles: MediaItem[];
  onDraftChange: (files: MediaItem[]) => void;
};

export const DialogBody = memo(
  ({ committedFilesLength, draftFiles, onDraftChange }: BodyProps) => {
    const { viewport } = useResponsive();

    const hasFiles = draftFiles.length > 0;
    const remainingSlots = 10 - committedFilesLength;

    const infoText = useMemo(() => {
      if (remainingSlots === draftFiles.length) {
        return `Вы уже прикрепили ${remainingSlots} ${integerDeclination(
          remainingSlots,
          ['вложение', 'вложения', 'вложений']
        )}, больше добавить нельзя`;
      }

      return `Выберите не более ${remainingSlots} ${integerDeclination(
        remainingSlots,
        ['вложения', 'вложений', 'вложений']
      )}`;
    }, [remainingSlots, draftFiles.length]);

    const handleDraftChange = useCallback(
      (
        items: MediaItem[],
        meta?: {
          action: 'add' | 'remove';
        }
      ) => {
        onDraftChange(items);

        if (meta?.action === 'remove') {
          message({
            type: 'success',
            text: 'Удалено',
          });
        }
      },
      []
    );

    return (
      <Media.Root
        initialItems={draftFiles}
        onItemsChange={handleDraftChange}
        canDelete
      >
        <VStack>
          <VStack spacing="xs">
            <HStack align="center" justify="space-between">
              <Text variant="heading-4" color="gray.500">
                Вложения
              </Text>

              {hasFiles && <ThumbnailsControls />}
            </HStack>

            {!hasFiles ? (
              <Empty />
            ) : (
              <Media.Thumbnails
                aspectRatio="3:4"
                viewMode={viewport.desktop ? 'grid' : 'carousel'}
                withPreviewer={false}
              />
            )}
          </VStack>

          <VStack spacing="xs">
            <UploadButton
              maxFiles={remainingSlots}
              isNoFiles={!hasFiles}
              onError={(text) =>
                message({
                  type: 'error',
                  text,
                })
              }
              style={{ width: '100%' }}
            />

            <Text variant="body-16" color="gray.500">
              {infoText}
            </Text>
          </VStack>
        </VStack>
      </Media.Root>
    );
  }
);
